import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import type {
    GeneratedInsight,
    InsightFamily,
    StandalonePackage,
    StandaloneRecommendation,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useEffect, useRef, useState } from "react";

const INITIAL_INSIGHTS_LOADER_PAGE_DATA = {
    size: 100,
    currentPage: 1,
    totalNumberOfInsights: 0,
};

export enum LazyLoadFetchMode {
    CLIENT = "CLIENT",
    SERVER = "SERVER",
}
interface LazyLoadPageData {
    size: number;
    currentPage: number;
    totalNumberOfInsights: number;
}

type UnifiedContent =
    | InsightFamily
    | TemplateAssociations
    | PitchedContent
    | GeneratedInsight
    | StandaloneRecommendation
    | StandalonePackage;

export const useClientSideLazyLoad = (
    data: UnifiedContent[],
    isLoaderInView: boolean,
): [UnifiedContent[], boolean] => {
    const [pageData, setPageData] = useState<LazyLoadPageData>({
        ...INITIAL_INSIGHTS_LOADER_PAGE_DATA,
    });
    const [lazyLoadedInsights, setLazyLoadedInsights] = useState<
        UnifiedContent[]
    >([]);
    const pageDataRef = useRef<LazyLoadPageData>(pageData);

    useEffect(() => {
        setPageData((prev) => ({
            ...prev,
            totalNumberOfInsights: data.length,
        }));
    }, [data]);

    useEffect(() => {
        pageDataRef.current = pageData;
    }, [pageData]);

    useEffect(() => {
        if (
            isLoaderInView &&
            data.length > 0 &&
            pageDataRef.current.size * pageDataRef.current.currentPage <
                pageDataRef.current.totalNumberOfInsights
        ) {
            const newPage = pageData.currentPage + 1;
            setPageData((prev) => ({ ...prev, currentPage: newPage }));
            setLazyLoadedInsights(
                data.slice(0, newPage * pageDataRef.current.size),
            );
        } else {
            setLazyLoadedInsights(
                data.slice(
                    0,
                    pageDataRef.current.currentPage * pageDataRef.current.size,
                ),
            );
        }
    }, [isLoaderInView, data]);

    return [lazyLoadedInsights, lazyLoadedInsights.length === data.length];
};
